"use client";

import { useEffect, useRef } from "react";

import { buildBayGeometry } from "../_lib/bay-map";
import { buildMapGeometry, type MapGeometry, routePoint } from "../_lib/us-map";

interface Props {
  className?: string;
  /** Colour of the static lattice. */
  ink?: string;
  /** Colour of the hubs that blink and of the signals travelling between them. */
  signal?: string;
  /** Colour of the venue hub everything converges on. */
  home?: string;
  /** Which map to draw. */
  region?: "us" | "bay";
}

interface HubState {
  level: number;
  nextFlash: number;
}
interface Pulse {
  route: number;
  t: number;
  duration: number;
}

const COLUMNS = 78;
const HUB_DECAY_MS = 260;
const TRAIL = 5;
/** How far the pointer reaches into the lattice, in CSS pixels. */
const INFLUENCE = 175;
/** How far a click ring travels, how wide its band is, and how long it takes. */
const RIPPLE_REACH = 620;
const RIPPLE_BAND = 96;
const RIPPLE_MS = 1150;

/**
 * Canvas fillStyle needs a colour it can parse itself; `color-mix()` is not
 * reliably supported there, so hex is expanded to rgba() by hand.
 */
function withAlpha(color: string, alpha: number): string {
  const hex = color.replace("#", "");
  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex;
  const value = Number.parseInt(full, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function ConvergenceMap({
  className,
  ink = "#1a1a1a",
  signal = "#4d6fff",
  home = "#0a06f7",
  region = "us",
}: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const hostNode = hostRef.current;
    const canvasNode = canvasRef.current;
    if (!hostNode || !canvasNode) {
      return;
    }

    // Re-bind through typed consts: the null narrowing above does not survive
    // into the hoisted helpers below.
    const host: HTMLDivElement = hostNode;
    const canvas: HTMLCanvasElement = canvasNode;
    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }
    const ctx: CanvasRenderingContext2D = context;

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    let reduced = motionQuery.matches;

    let geometry: MapGeometry | null = null;
    let plate: HTMLCanvasElement | null = null;
    let hubs: HubState[] = [];
    let pulses: Pulse[] = [];
    let nextPulse = 0;
    let raf = 0;
    let last = 0;
    let onScreen = true;

    // Pointer influence: the target is where the cursor is, the current value
    // eases toward it so the lit patch trails rather than snaps.
    let pointerX = -9999;
    let pointerY = -9999;
    let litX = -9999;
    let litY = -9999;
    let litStrength = 0;
    let pointerInside = false;
    // a click sends a ring out across the lattice from where it landed
    let ripples: { x: number; y: number; t: number }[] = [];

    function buildPlate(geo: MapGeometry, dpr: number) {
      const layer = document.createElement("canvas");
      layer.width = Math.round(geo.width * dpr);
      layer.height = Math.round(geo.height * dpr);
      const pen = layer.getContext("2d");
      if (!pen) {
        return null;
      }
      pen.setTransform(dpr, 0, 0, dpr, 0, 0);

      // The routes are static, so they belong on the plate rather than the loop.
      pen.strokeStyle = withAlpha(home, 0.16);
      pen.lineWidth = 1;
      for (const route of geo.routes) {
        pen.beginPath();
        pen.moveTo(route.x0, route.y0);
        pen.quadraticCurveTo(route.cx, route.cy, route.x1, route.y1);
        pen.stroke();
      }

      const { base, alpha, radius } = geo;
      if (alpha) {
        for (let i = 0; i < base.length; i += 2) {
          pen.fillStyle = withAlpha(ink, 0.34 * (alpha[i / 2] as number));
          pen.beginPath();
          pen.arc(
            base[i] as number,
            base[i + 1] as number,
            radius,
            0,
            Math.PI * 2,
          );
          pen.fill();
        }
      } else {
        pen.fillStyle = withAlpha(ink, 0.24);
        for (let i = 0; i < base.length; i += 2) {
          pen.beginPath();
          pen.arc(
            base[i] as number,
            base[i + 1] as number,
            radius,
            0,
            Math.PI * 2,
          );
          pen.fill();
        }
      }
      return layer;
    }

    function layout() {
      const width = host.clientWidth;
      if (!width) {
        return;
      }
      const pitch = Math.max(9, Math.min(17, width / COLUMNS));
      const geo =
        region === "bay"
          ? buildBayGeometry(width, pitch)
          : buildMapGeometry(width, pitch);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);

      canvas.width = Math.round(geo.width * dpr);
      canvas.height = Math.round(geo.height * dpr);
      canvas.style.width = `${geo.width}px`;
      canvas.style.height = `${geo.height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      geometry = geo;
      plate = buildPlate(geo, dpr);

      if (hubs.length !== geo.hubs.length) {
        // Stagger the first flash, or every hub lights up together on frame one.
        const t0 = performance.now();
        hubs = geo.hubs.map((hub) => ({
          level: hub.home ? 0.6 : 0.35,
          nextFlash: t0 + Math.random() * 3500,
        }));
        pulses = [];
        nextPulse = t0 + 600;
      }
    }

    /** Redraw the dots the pointer is near, larger and in the signal colour. */
    function drawPointerField(geo: MapGeometry) {
      if (litStrength <= 0.01) {
        return;
      }
      const { base, alpha, radius } = geo;
      const reachSq = INFLUENCE * INFLUENCE;
      for (let i = 0; i < base.length; i += 2) {
        const dx = (base[i] as number) - litX;
        const dy = (base[i + 1] as number) - litY;
        const dSq = dx * dx + dy * dy;
        if (dSq > reachSq) {
          continue;
        }
        const falloff = (1 - Math.sqrt(dSq) / INFLUENCE) ** 1.6 * litStrength;
        if (falloff <= 0.02) {
          continue;
        }
        const own = alpha ? (alpha[i / 2] as number) : 1;
        ctx.fillStyle = withAlpha(signal, Math.min(0.95, falloff * own * 1.15));
        ctx.beginPath();
        ctx.arc(
          base[i] as number,
          base[i + 1] as number,
          radius * (1 + falloff * 1.35),
          0,
          Math.PI * 2,
        );
        ctx.fill();
      }
    }

    /** Redraw the dots a click ring is passing over. */
    function drawRipples(geo: MapGeometry) {
      if (!ripples.length) {
        return;
      }
      const { base, alpha, radius } = geo;
      for (const ripple of ripples) {
        const ring = ripple.t * RIPPLE_REACH;
        const fade = (1 - ripple.t) ** 1.4;
        const lo = ring - RIPPLE_BAND;
        const hi = ring + RIPPLE_BAND;
        for (let i = 0; i < base.length; i += 2) {
          const dx = (base[i] as number) - ripple.x;
          if (dx < -hi || dx > hi) {
            continue;
          }
          const dy = (base[i + 1] as number) - ripple.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < lo || d > hi) {
            continue;
          }
          const k = (1 - Math.abs(d - ring) / RIPPLE_BAND) ** 1.6 * fade;
          if (k <= 0.02) {
            continue;
          }
          const own = alpha ? (alpha[i / 2] as number) : 1;
          ctx.fillStyle = withAlpha(signal, Math.min(0.95, k * own));
          ctx.beginPath();
          ctx.arc(
            base[i] as number,
            base[i + 1] as number,
            radius * (1 + k * 1.5),
            0,
            Math.PI * 2,
          );
          ctx.fill();
        }
      }
    }

    function draw() {
      const geo = geometry;
      if (!geo || !plate) {
        return;
      }
      const { radius } = geo;

      ctx.clearRect(0, 0, geo.width, geo.height);
      ctx.drawImage(plate, 0, 0, geo.width, geo.height);

      drawPointerField(geo);
      drawRipples(geo);

      for (let i = 0; i < geo.hubs.length; i++) {
        const hub = geo.hubs[i];
        const state = hubs[i];
        if (!hub || !state) {
          continue;
        }
        const level = state.level;

        if (hub.home) {
          ctx.globalAlpha = 1;
          ctx.fillStyle = home;
          ctx.beginPath();
          ctx.arc(
            hub.x,
            hub.y,
            radius * 1.7 + radius * 0.5 * level,
            0,
            Math.PI * 2,
          );
          ctx.fill();

          ctx.globalAlpha = 0.32 + 0.5 * level;
          ctx.strokeStyle = home;
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.arc(
            hub.x,
            hub.y,
            radius * 3.2 + radius * 2.6 * level,
            0,
            Math.PI * 2,
          );
          ctx.stroke();
        } else {
          ctx.globalAlpha = 0.42 + 0.58 * level;
          ctx.fillStyle = signal;
          ctx.beginPath();
          ctx.arc(
            hub.x,
            hub.y,
            radius * 1.2 + radius * 0.8 * level,
            0,
            Math.PI * 2,
          );
          ctx.fill();

          if (level > 0.06) {
            ctx.globalAlpha = level * 0.4;
            ctx.strokeStyle = signal;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.arc(
              hub.x,
              hub.y,
              radius * 2 + radius * 5 * (1 - level),
              0,
              Math.PI * 2,
            );
            ctx.stroke();
          }
        }
      }

      ctx.fillStyle = signal;
      for (const pulse of pulses) {
        const route = geo.routes[pulse.route];
        if (!route) {
          continue;
        }
        for (let k = 0; k < TRAIL; k++) {
          const t = pulse.t - k * 0.022;
          if (t < 0) {
            break;
          }
          const [x, y] = routePoint(route, t);
          ctx.globalAlpha = (1 - k / TRAIL) * 0.85;
          ctx.beginPath();
          ctx.arc(x, y, radius * (1.05 - k * 0.14), 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
    }

    function step(now: number) {
      const geo = geometry;
      if (!geo) {
        return;
      }
      const dt = Math.min(50, last ? now - last : 16);
      last = now;

      if (pointerInside) {
        if (litStrength <= 0.01) {
          litX = pointerX;
          litY = pointerY;
        }
        litX += (pointerX - litX) * 0.16;
        litY += (pointerY - litY) * 0.16;
        litStrength += (1 - litStrength) * 0.1;
      } else {
        litStrength += (0 - litStrength) * 0.08;
      }

      const decay = Math.exp(-dt / HUB_DECAY_MS);
      for (let i = 0; i < hubs.length; i++) {
        const state = hubs[i];
        const hub = geo.hubs[i];
        if (!state || !hub) {
          continue;
        }
        state.level *= decay;
        if (hub.home) {
          continue;
        }
        if (now >= state.nextFlash) {
          state.level = 1;
          // every hub keeps its own random cadence, so the field never pulses in unison
          state.nextFlash = now + 1400 + Math.random() * 4600;
        }
      }

      if (now >= nextPulse && geo.routes.length) {
        pulses.push({
          route: Math.floor(Math.random() * geo.routes.length),
          t: 0,
          duration: 1300 + Math.random() * 700,
        });
        nextPulse = now + 420 + Math.random() * 680;
      }

      ripples = ripples.filter((ripple) => {
        ripple.t += dt / RIPPLE_MS;
        return ripple.t < 1;
      });

      const homeState = hubs.find((_, i) => geo.hubs[i]?.home);
      pulses = pulses.filter((pulse) => {
        pulse.t += dt / pulse.duration;
        if (pulse.t >= 1) {
          // arrival is what makes the venue flash, so convergence is visible
          if (homeState) {
            homeState.level = 1;
          }
          return false;
        }
        return true;
      });

      draw();
      raf = requestAnimationFrame(step);
    }

    function start() {
      if (raf || reduced || !onScreen || document.hidden) {
        return;
      }
      last = 0;
      raf = requestAnimationFrame(step);
    }

    function stop() {
      if (raf) {
        cancelAnimationFrame(raf);
      }
      raf = 0;
    }

    function restart() {
      stop();
      layout();
      if (reduced) {
        for (let i = 0; i < hubs.length; i++) {
          const state = hubs[i];
          if (state) {
            state.level = geometry?.hubs[i]?.home ? 0.6 : 0.45;
          }
        }
        draw();
        return;
      }
      start();
    }

    // The canvas sits behind the hero copy and takes no pointer events itself,
    // so the window is what reports where the cursor is.
    function onPointerMove(event: PointerEvent) {
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      pointerInside =
        x > -INFLUENCE &&
        y > -INFLUENCE &&
        x < rect.width + INFLUENCE &&
        y < rect.height + INFLUENCE;
      pointerX = x;
      pointerY = y;
    }

    function onPointerLeave() {
      pointerInside = false;
    }

    // Clicking the field sends a ring out from the point. Anything the reader
    // was actually aiming at keeps its click to itself.
    function onPointerDown(event: PointerEvent) {
      if (reduced || ripples.length > 3) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target?.closest("a, button, input, label, textarea, select")) {
        return;
      }
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
        return;
      }
      ripples.push({ x, y, t: 0 });
      start();
    }

    let lastWidth = 0;
    const resizeObserver = new ResizeObserver(() => {
      const next = host.clientWidth;
      if (next === lastWidth) {
        return;
      }
      lastWidth = next;
      restart();
    });
    resizeObserver.observe(host);

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        onScreen = entries.some((entry) => entry.isIntersecting);
        if (onScreen) {
          start();
        } else {
          stop();
        }
      },
      { rootMargin: "120px" },
    );
    intersectionObserver.observe(host);

    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    };
    const onMotionChange = () => {
      reduced = motionQuery.matches;
      restart();
    };

    document.addEventListener("visibilitychange", onVisibility);
    motionQuery.addEventListener("change", onMotionChange);
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("pointerdown", onPointerDown, { passive: true });
    document.addEventListener("pointerleave", onPointerLeave);
    restart();

    return () => {
      stop();
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      motionQuery.removeEventListener("change", onMotionChange);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("pointerleave", onPointerLeave);
    };
  }, [ink, signal, home, region]);

  return (
    <div
      ref={hostRef}
      className={className}
      style={{ aspectRatio: region === "bay" ? "1200 / 928" : "1200 / 749" }}
      aria-hidden="true"
    >
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  );
}
